import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CompareContext = createContext(null);

const COMPARE_STORAGE_KEY = 'compare_product_ids';
const MAX_COMPARE_PRODUCTS = 4;

/**
 * Helper functions for localStorage
 */
const getFromStorage = () => {
  try {
    const stored = localStorage.getItem(COMPARE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const setInStorage = (productIds) => {
  try {
    localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(productIds));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Compare Context Provider
 * Manages product comparison state with localStorage persistence
 */
export function CompareProvider({ children }) {
  const [productIds, setProductIds] = useState(() => getFromStorage());
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Persist to localStorage on changes
  useEffect(() => {
    setInStorage(productIds);
  }, [productIds]);

  // Add product to comparison
  const addToCompare = useCallback((productId) => {
    setProductIds((prev) => {
      if (prev.includes(productId)) {
        return prev;
      }
      if (prev.length >= MAX_COMPARE_PRODUCTS) {
        return prev;
      }
      return [...prev, productId];
    });
  }, []);

  // Remove product from comparison
  const removeFromCompare = useCallback((productId) => {
    setProductIds((prev) => prev.filter((id) => id !== productId));
  }, []);

  // Toggle product in comparison
  const toggleCompare = useCallback((productId) => {
    setProductIds((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      }
      if (prev.length >= MAX_COMPARE_PRODUCTS) {
        return prev;
      }
      return [...prev, productId];
    });
  }, []);

  // Clear all products from comparison
  const clearCompare = useCallback(() => {
    setProductIds([]);
  }, []);

  // Check if product is in comparison
  const isInCompare = useCallback(
    (productId) => productIds.includes(productId),
    [productIds]
  );

  // Check if comparison is full
  const isFull = productIds.length >= MAX_COMPARE_PRODUCTS;

  // Open/close drawer
  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const value = {
    productIds,
    count: productIds.length,
    maxProducts: MAX_COMPARE_PRODUCTS,
    isFull,
    isDrawerOpen,
    addToCompare,
    removeFromCompare,
    toggleCompare,
    clearCompare,
    isInCompare,
    openDrawer,
    closeDrawer
  };

  return (
    <CompareContext.Provider value={value}>
      {children}
    </CompareContext.Provider>
  );
}

/**
 * Hook to access compare context
 * @returns {Object} Compare context value
 */
export function useCompare() {
  const context = useContext(CompareContext);

  if (!context) {
    throw new Error('useCompare must be used within CompareProvider');
  }

  return context;
}
